import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { creditOperationFor, reportTypesFor, type ReportTypeDef } from '@ni/shared';
import { useApi, useOrg } from '../../api/app-context';
import { useCreditPrice, useCreditShortfall } from '../../api/hooks';
import { useCreateReport } from '../../api/report-hooks';
import { pricedLabel } from '../../lib/copy';
import { ReportInputsForm } from '../../components/reports/report-inputs-form';
import type { PickedPerson } from '../../components/reports/people-picker';
import { REPORTS_TITLE } from '../../lib/report-copy';
import { Loading } from '../../components/ui/loading';
import './reports.css';
import { TopBar } from '../../components/ui/top-bar';

/** The new-dossier form for one person, with their account already chosen. */
export function newDossierPath(entityId: string, accountId: string): string {
  return `/reports/new?${new URLSearchParams({
    type: 'dossier',
    account: accountId,
    person: entityId,
  }).toString()}`;
}

/**
 * The person a report was opened for (a person page's "Prepare a dossier"),
 * read before the form opens so the picker starts with them. An id that does
 * not resolve is ignored and the picker starts empty.
 */
function WithPerson({
  entityId,
  children,
}: {
  entityId: string;
  children: (people: PickedPerson[]) => React.ReactNode;
}) {
  /* Read once for the name: the record page's own query polls, and the form
     keeps the person it opened with. */
  const api = useApi();
  const org = useOrg();
  const record = useQuery({
    queryKey: ['report-new-person', org.id, entityId],
    queryFn: () => api.getRecord(entityId),
    staleTime: Infinity,
  });
  if (record.isPending)
    return (
      <p className="lede">
        <Loading />
      </p>
    );
  return (
    <>
      {children(
        record.data ? [{ entity_id: record.data.subject.id, name: record.data.subject.name }] : [],
      )}
    </>
  );
}

/**
 * **New report**: pick a type (always shown, even with one), then the people and
 * the type's own prose fields. Pressing the button starts the first version.
 */
export function NewReport() {
  const org = useOrg();
  const navigate = useNavigate();
  const types = reportTypesFor(org.vertical);
  /* Opened from an event's Analysis tab, the type, account and event arrive
     chosen, and from a person page the type, account and person; otherwise the type is always asked, even with one: the choice is
     the first thing the office makes. */
  const [params] = useSearchParams();
  const [picked, setPicked] = useState<ReportTypeDef | null>(
    types.find((type) => type.key === params.get('type')) ?? null,
  );
  const create = useCreateReport();
  // A travel brief is priced on an enrolled account; every other type is free.
  const price = useCreditPrice(creditOperationFor('report_generate', { type: picked?.key }));
  const short = useCreditShortfall(price.units);
  if (types.length === 0) return <Navigate to="/" replace />;
  const personId = params.get('person');
  const withPeople = (form: (people: PickedPerson[]) => React.ReactNode) =>
    personId ? <WithPerson entityId={personId}>{form}</WithPerson> : form([]);

  return (
    <div className="column">
      <TopBar parent={{ to: '/reports', label: REPORTS_TITLE }} />
      <div className="head">
        <div className="titles">
          <div className="title">
            {picked ? `New ${picked.display_name.toLowerCase()}` : 'New report'}
          </div>
        </div>
      </div>

      {picked === null ? (
        <ul className="report-types">
          {types.map((type) => (
            <li key={type.key}>
              <button type="button" className="report-type" onClick={() => setPicked(type)}>
                <span className="report-type-name">{type.display_name}</span>
                <span className="report-type-desc">{type.description}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <p className="lede">{picked.description}</p>
          {withPeople((initialPeople) => (
            <ReportInputsForm
              type={picked}
              initialPeople={initialPeople}
              initialAccountId={params.get('account') ?? ''}
              initialEventId={params.get('event') ?? ''}
              withTitle
              submitLabel={pricedLabel('Prepare the report', price.units, price.unitsPerCredit)}
              creditShort={short}
              pending={create.isPending}
              error={create.error}
              onSubmit={({ title, accountId, eventId, people, inputs }) =>
                create.mutate(
                  {
                    type: picked.key,
                    ...(title ? { title } : {}),
                    ...(accountId ? { account_id: accountId } : {}),
                    event_id: eventId,
                    people: people.map((p) => p.entity_id),
                    inputs,
                    ...(price.units === undefined
                      ? {}
                      : { expected_credit_units: price.units ?? 0 }),
                  },
                  { onSuccess: (started) => navigate(`/reports/${started.report_id}`) },
                )
              }
              onCancel={() => navigate('/reports')}
            />
          ))}
        </>
      )}
    </div>
  );
}
